import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { formatInteger } from "../helpers/formatNumber";
import { UsersAdminService } from "../api/generated/services/UsersAdminService";
import { ErasureListItemViewModel } from "../api/generated/models/ErasureListItemViewModel";
import { ErasureSummaryViewModel } from "../api/generated/models/ErasureSummaryViewModel";
import { ErasureStatus } from "../api/generated/models/ErasureStatus";
import { formatCopenhagenDateTime } from "../helpers/dateFilters";
import "../api/openApiConfig";
import ErasureDetailsModal from "../components/AdminErasures/ErasureDetailsModal";

const PAGE_SIZE = 50;

const isErasureStatus = (value: string): value is ErasureStatus =>
  (Object.values(ErasureStatus) as string[]).includes(value);

const statusBadgeClass = (status?: ErasureStatus) => {
  switch (status) {
    case ErasureStatus.COMPLETED:
      return "bg-green-100 text-green-800";
    case ErasureStatus.IN_PROGRESS:
      return "bg-blue-100 text-blue-800";
    case ErasureStatus.BLOCKED:
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const Counter: React.FC<{
  id: string;
  label: string;
  value?: number;
  hint?: string;
  emphasis?: boolean;
}> = ({ id, label, value, hint, emphasis }) => {
  const alarming = Boolean(emphasis) && (value ?? 0) > 0;
  return (
    <div
      data-testid={`erasure-counter-${id}`}
      className={`bg-white rounded-lg shadow p-4 min-w-[150px] ${
        alarming ? "ring-2 ring-red-400" : ""
      }`}
    >
      <div className="text-gray-500 text-sm">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${alarming ? "text-red-700" : ""}`}>
        {formatInteger(value ?? 0)}
      </div>
      {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
    </div>
  );
};

const AdminErasures: React.FC = () => {
  const [summary, setSummary] = useState<ErasureSummaryViewModel | null>(null);
  const [rows, setRows] = useState<ErasureListItemViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openIdentityId, setOpenIdentityId] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  // URL is the single source of truth for the applied filter and page, matching AdminUsers.
  const statusParam = searchParams.get("status") ?? "";
  const appliedStatus = isErasureStatus(statusParam) ? statusParam : undefined;
  const pageRaw = parseInt(searchParams.get("page") ?? "1", 10);
  const pageNumber = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;

  const buildParams = (status: string, page: number): URLSearchParams => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (page > 1) params.set("page", String(page));
    return params;
  };

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const [summaryResponse, listResponse] = await Promise.all([
          UsersAdminService.postApiUsersAdminErasureSummary({}),
          UsersAdminService.postApiUsersAdminListErasures({
            status: appliedStatus,
            pagination: { pageNumber: pageNumber - 1, pageSize: PAGE_SIZE },
          }),
        ]);
        if (ignore) return;
        setSummary(summaryResponse);
        setRows(listResponse.data ?? []);
        setTotal(listResponse.total ?? 0);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch erasures");
        console.error("Error fetching erasures:", err);
      } finally {
        if (!ignore) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex flex-col gap-4 mb-8">
        <h2 className="font-medium">Erasures</h2>

        <div className="flex flex-wrap gap-4">
          <Counter
            id="waitingGracePeriod"
            label="In grace period"
            value={summary?.waitingGracePeriod}
            hint="Can still be reactivated"
          />
          <Counter
            id="pending"
            label="Pending"
            value={summary?.pending}
            hint="Past due, awaiting the worker"
          />
          <Counter id="inProgress" label="In progress" value={summary?.inProgress} />
          <Counter
            id="blocked"
            label="Blocked"
            value={summary?.blocked}
            hint="Needs a human"
            emphasis
          />
          <Counter
            id="completedLast24Hours"
            label="Done (24h)"
            value={summary?.completedLast24Hours}
          />
          <Counter
            id="completedLast7Days"
            label="Done (7d)"
            value={summary?.completedLast7Days}
          />
          <Counter id="completed" label="Done (total)" value={summary?.completed} />
        </div>

        <div data-testid="erasure-oldest-pending" className="text-sm text-gray-500">
          Oldest pending account due since{" "}
          <span className="font-medium text-gray-700">
            {formatCopenhagenDateTime(summary?.oldestPendingDeleteAccountAfter as any)}
          </span>
        </div>

        <div className="w-[240px]">
          <label
            htmlFor="erasure-status-filter"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Status
          </label>
          <select
            id="erasure-status-filter"
            className="w-full p-2 border border-gray-300 rounded-md bg-white h-[42px]"
            value={appliedStatus ?? ""}
            // Any filter change resets to page one: page 4 of the unfiltered list is
            // meaningless once the result set changes underneath it.
            onChange={(e) => setSearchParams(buildParams(e.target.value, 1))}
          >
            <option value="">All statuses</option>
            <option value={ErasureStatus.NOT_STARTED}>Not started</option>
            <option value={ErasureStatus.IN_PROGRESS}>In progress</option>
            <option value={ErasureStatus.COMPLETED}>Completed</option>
            <option value={ErasureStatus.BLOCKED}>Blocked</option>
          </select>
        </div>
      </div>

      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      <div className="overflow-x-auto">
        <table className="table-default">
          <thead>
            <tr>
              <th>Account</th>
              <th>Status</th>
              <th className="whitespace-nowrap">Due since</th>
              <th className="whitespace-nowrap">Days overdue</th>
              <th className="whitespace-nowrap">Steps</th>
              <th className="whitespace-nowrap">Stopped at</th>
              <th className="whitespace-nowrap">Completed</th>
              <th>
                <span className="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.identityId} data-testid={`erasure-row-${row.identityId}`}>
                <td className="font-mono text-xs whitespace-nowrap">{row.identityId}</td>
                <td>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(
                      row.status
                    )}`}
                  >
                    {row.status}
                  </span>
                </td>
                <td className="whitespace-nowrap">
                  {formatCopenhagenDateTime(row.deleteAccountAfter as any)}
                </td>
                <td>{row.daysOverdue ?? 0}</td>
                <td className="whitespace-nowrap">
                  {row.stepsCompleted ?? 0}/{row.stepsTotal ?? 0}
                </td>
                <td className="whitespace-nowrap">
                  {row.lastFailedStep ? (
                    <>
                      {row.lastFailedStep}
                      <span className="text-xs text-gray-400 ml-1">
                        ({row.lastFailedAttempts ?? 0} attempts)
                      </span>
                    </>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {formatCopenhagenDateTime(row.erasureCompletedAt as any)}
                </td>
                <td className="whitespace-nowrap">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`erasure-details-${row.identityId}`}
                    onClick={() => setOpenIdentityId(row.identityId ?? null)}
                  >
                    Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && !loading && (
        <div className="text-gray-500 mt-4">No accounts match this filter.</div>
      )}

      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={() =>
            setSearchParams(buildParams(appliedStatus ?? "", Math.max(1, pageNumber - 1)))
          }
          disabled={pageNumber === 1}
          variant="outline"
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
        <Button
          onClick={() => setSearchParams(buildParams(appliedStatus ?? "", pageNumber + 1))}
          disabled={pageNumber >= totalPages}
          variant="outline"
        >
          Next
        </Button>
      </div>

      <ErasureDetailsModal
        isOpen={openIdentityId !== null}
        identityId={openIdentityId}
        onClose={() => setOpenIdentityId(null)}
      />
    </div>
  );
};

export default AdminErasures;
